import { useEffect, useState } from "react";
import { Copy, ExternalLink, LogIn, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Panel } from "@/components/PageHeader";
import { SmtpSettings } from "@/components/admin/SmtpSettings";
import { PasswordStrength } from "@/components/PasswordStrength";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fdate } from "@/lib/api";
import { loginUrl, switchTenant } from "@/lib/tenant";

const NEW = { name: "", slug: "", active: true, admin_name: "", admin_email: "", admin_password: "" };
const toSlug = (v) => v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

const F = ({ label, children }) => <label className="block min-w-0 text-xs text-slate-500">{label}<div className="mt-1">{children}</div></label>;

const TenantDialog = ({ t, onClose, onSaved }) => {
  const [f, setF] = useState(t);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(t), [t]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const body = f.id ? { name: f.name, slug: f.slug, active: f.active } : f;
      if (f.id) await api.put(`/tenants/${f.id}`, body); else await api.post("/tenants", body);
      toast.success(f.id ? "Circuito aggiornato" : "Circuito creato con dati di esempio"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!t} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="tenant-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Modifica circuito" : "Nuovo circuito"}</DialogTitle></DialogHeader>
        <div className="grid min-w-0 grid-cols-1 gap-3">
          <F label="Nome del circuito"><Input data-testid="tenant-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: f.id || f.slugTouched ? f.slug : toSlug(e.target.value) })} /></F>
          <F label="Indirizzo di accesso dedicato">
            <div className="flex items-center rounded-md border border-input text-sm"><span className="min-w-0 shrink truncate pl-3 text-slate-400">{window.location.host}/login/</span>
              <input data-testid="tenant-slug-input" className="h-9 min-w-0 flex-1 bg-transparent pr-3 outline-none" value={f.slug} onChange={(e) => setF({ ...f, slug: toSlug(e.target.value), slugTouched: true })} /></div>
          </F>
          {!f.id && <>
            <div className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Amministratore del circuito</div>
            <F label="Nome e cognome"><Input data-testid="tenant-admin-name-input" value={f.admin_name} onChange={set("admin_name")} /></F>
            <F label="Email"><Input type="email" data-testid="tenant-admin-email-input" value={f.admin_email} onChange={set("admin_email")} /></F>
            <F label="Password iniziale"><Input type="password" autoComplete="new-password" data-testid="tenant-admin-password-input" value={f.admin_password} onChange={set("admin_password")} /><PasswordStrength value={f.admin_password} /></F>
            <p className="text-xs text-slate-400">Il circuito parte con dati di esempio: entra nel circuito e collega la sua fonte dati da "Connessione API".</p>
          </>}
          {f.id && <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">Circuito attivo<Switch data-testid="tenant-active-switch" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></label>}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button data-testid="tenant-save-button" disabled={busy} className="bg-blue-600 hover:bg-blue-700" onClick={save}>{busy ? "Salvataggio..." : "Salva circuito"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ t, current, onEdit }) => (
  <tr className="border-t border-slate-100" data-testid={`tenant-row-${t.slug}`}>
    <td className="px-5 py-3"><div className="font-medium text-slate-900">{t.name}</div><div className="text-xs text-slate-400">Creato il {fdate(t.created_at)}</div></td>
    <td className="px-5 py-3"><button type="button" data-testid={`tenant-copy-link-${t.slug}`} onClick={() => { navigator.clipboard.writeText(loginUrl(t.slug)); toast.success("Link di accesso copiato"); }}
      className="inline-flex items-center gap-1.5 rounded bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600 hover:text-blue-600">/login/{t.slug}<Copy className="h-3 w-3" /></button></td>
    <td className="px-5 py-3 text-sm">{t.source === "external" ? <span className="text-emerald-700">API esterna</span> : <span className="text-slate-500">Dati di esempio</span>}
      {t.source_url && <div className="max-w-[200px] truncate text-xs text-slate-400">{t.source_url}</div>}</td>
    <td className="px-5 py-3 text-sm tabular-nums">{t.users}</td>
    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${t.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{t.active ? "Attivo" : "Disattivato"}</span></td>
    <td className="px-5 py-3 text-right whitespace-nowrap">
      <Button size="sm" variant="ghost" data-testid={`tenant-edit-${t.slug}`} onClick={() => onEdit(t)}><Pencil className="h-4 w-4" /></Button>
      <Button size="sm" variant="outline" disabled={current} data-testid={`tenant-enter-${t.slug}`} onClick={() => switchTenant(t.id)}>
        {current ? "Sei qui" : <><LogIn className="mr-1 h-4 w-4" />Entra</>}</Button>
    </td>
  </tr>
);

export default function Circuits() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/tenants").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="circuits-page">
      <PageHeader title="Circuiti" subtitle="Ogni circuito ha fonte dati, utenti, campagne, report e branding separati. Solo tu vedi tutti i circuiti.">
        <Button data-testid="tenant-new-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => setEdit({ ...NEW })}><Plus className="mr-1 h-4 w-4" />Nuovo circuito</Button>
      </PageHeader>
      <Panel testId="tenants-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead><tr className="text-xs uppercase tracking-wider text-slate-400">
              <th className="px-5 py-3 font-medium">Circuito</th><th className="px-5 py-3 font-medium">Link di accesso</th><th className="px-5 py-3 font-medium">Fonte dati</th>
              <th className="px-5 py-3 font-medium">Utenti</th><th className="px-5 py-3 font-medium">Stato</th><th className="px-5 py-3" /></tr></thead>
            <tbody>{rows.map((t) => <Row key={t.id} t={t} current={t.id === user?.tenant?.id} onEdit={setEdit} />)}</tbody>
          </table>
        </div>
      </Panel>
      <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400"><ExternalLink className="h-3.5 w-3.5" />Condividi il link di accesso con gli utenti del circuito: vedranno logo e colori del circuito già nella pagina di login.</p>
      <div className="mt-6"><SmtpSettings /></div>
      <TenantDialog t={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
